import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileSpreadsheet, Loader2, CalendarClock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const WINDOWS = [30, 60, 90, 180];

export default function OpsScheduleCard() {
  const [items, setItems] = useState(null);
  const [totalEvents, setTotalEvents] = useState(0);
  const [customer, setCustomer] = useState('__all__');
  const [days, setDays] = useState('90');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/pollen/ops-schedule/customers?days=${days}`, { credentials: 'include', headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => {
        const list = d.items || [];
        setItems(list);
        setTotalEvents(d.total_events || 0);
        setCustomer((c) => (c !== '__all__' && !list.some((x) => x.customer === c) ? '__all__' : c));
      })
      .catch(() => setItems([]));
  }, [days]);

  const download = async () => {
    setBusy(true);
    try {
      const custQ = customer !== '__all__' ? `&customer=${encodeURIComponent(customer)}` : '';
      const r = await fetch(`${API}/api/pollen/ops-schedule.xlsx?days=${days}${custQ}`, { credentials: 'include', headers: authHeaders() });
      if (!r.ok) throw new Error('Export failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pollen-ops-schedule${customer !== '__all__' ? '-' + customer.toLowerCase().replace(/\s+/g, '-') : ''}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(customer !== '__all__' ? `Schedule for ${customer} downloaded` : 'Full ops schedule downloaded');
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <Card data-testid="ops-schedule-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-1.5">
          <CalendarClock className="w-4 h-4 text-emerald-700" />Check-ins &amp; Pickups — Excel
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-[11px] text-muted-foreground">
          {items === null ? 'Loading…'
            : items.length === 0 ? `No upcoming hotel check-ins/check-outs or pickups in the next ${days} days.`
            : `${totalEvents} upcoming event${totalEvents === 1 ? '' : 's'} across ${items.length} customer${items.length === 1 ? '' : 's'} in the next ${days} days.`}
        </p>
        <div className="flex gap-2">
          <Select value={customer} onValueChange={setCustomer}>
            <SelectTrigger className="flex-1 h-9" data-testid="ops-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All customers (one sheet each)</SelectItem>
              {(items || []).map((c) => (
                <SelectItem key={c.customer} value={c.customer}>{c.customer}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-24 h-9" data-testid="ops-days-select"><SelectValue /></SelectTrigger>
            <SelectContent>{WINDOWS.map((w) => <SelectItem key={w} value={String(w)}>{w} days</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button size="sm" className="w-full" onClick={download} disabled={busy || !items || items.length === 0} data-testid="ops-download-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}
          Download {customer !== '__all__' ? `${customer}'s sheet` : 'Excel (all customers)'}
        </Button>
      </CardContent>
    </Card>
  );
}
